import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { Activity, ActivityType } from '../../api/activities';
import { CutApprovals } from './CutApprovals';
import { Tabs, TabPanel } from '../ui/Tabs';
import { Card } from '../ui/Card';
import {
  FileAudio,
  FolderPlus,
  Music,
  UserPlus,
  MessageSquare,
  FolderOpen,
  FileText,
  Share2,
  Loader2,
  ArrowRightLeft,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

interface CutOverviewProps {
  cutId: string;
}

type OverviewSubTab = 'activity' | 'approvals';

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function getActivityIcon(type: ActivityType) {
  const iconClass = 'w-5 h-5';
  switch (type) {
    case 'file_uploaded':
      return <FileAudio className={iconClass} />;
    case 'cut_created':
      return <Music className={iconClass} />;
    case 'cut_moved':
      return <ArrowRightLeft className={iconClass} />;
    case 'vibe_created':
      return <FolderPlus className={iconClass} />;
    case 'project_created':
      return <FolderOpen className={iconClass} />;
    case 'member_added':
      return <UserPlus className={iconClass} />;
    case 'comment_added':
    case 'public_comment_added':
      return <MessageSquare className={iconClass} />;
    case 'lyrics_updated':
      return <FileText className={iconClass} />;
    case 'file_shared':
      return <Share2 className={iconClass} />;
    case 'approval_requested':
      return <CheckCircle2 className={iconClass} />;
    case 'approval_resolved':
      return <XCircle className={iconClass} />;
    default:
      return <Music className={iconClass} />;
  }
}

function getActivityIconColor(type: ActivityType): string {
  switch (type) {
    case 'file_uploaded':
    case 'file_shared':
      return 'bg-blue-500/10 text-blue-500';
    case 'cut_created':
    case 'vibe_created':
      return 'bg-purple-500/10 text-purple-500';
    case 'cut_moved':
      return 'bg-amber-500/10 text-amber-500';
    case 'project_created':
      return 'bg-green-500/10 text-green-500';
    case 'member_added':
      return 'bg-yellow-500/10 text-yellow-500';
    case 'comment_added':
    case 'public_comment_added':
      return 'bg-pink-500/10 text-pink-500';
    case 'lyrics_updated':
      return 'bg-orange-500/10 text-orange-500';
    case 'approval_requested':
      return 'bg-yellow-500/10 text-yellow-500';
    case 'approval_resolved':
      return 'bg-green-500/10 text-green-500';
    default:
      return 'bg-primary/10 text-primary';
  }
}

function getActivityDescription(activity: Activity): string {
  const metadata = activity.metadata ? JSON.parse(activity.metadata) : {};

  switch (activity.type) {
    case 'file_uploaded':
      return `uploaded ${metadata.fileName || 'a file'}`;
    case 'cut_created':
      return `created this cut`;
    case 'cut_moved':
      return `moved this cut from ${metadata.fromVibeName || 'another vibe'} to ${metadata.toVibeName || 'this vibe'}`;
    case 'comment_added':
      return `commented on ${metadata.cutName || 'this cut'}`;
    case 'public_comment_added':
      return `commented on a shared file`;
    case 'lyrics_updated':
      return `updated lyrics`;
    case 'file_shared':
      return `shared ${metadata.fileName || 'a file'}`;
    case 'approval_requested':
      if (metadata.targetUserName) {
        return `requested approval from ${metadata.targetUserName}`;
      }
      return `requested feedback from the band`;
    case 'approval_resolved':
      return `${metadata.status === 'APPROVED' ? 'approved' : 'rejected'} ${metadata.requesterName}'s suggestion`;
    default:
      return 'performed an action';
  }
}

function ActivityFeed({ cutId }: { cutId: string }) {
  const { activities, isLoadingActivities, markActivityAsRead } = useSocket();
  const navigate = useNavigate();

  // Filter activities that are related to this cut
  const cutActivities = activities.filter((activity) => {
    if (!activity.resourceLink) return false;
    return activity.resourceLink.startsWith(`/cuts/${cutId}`) || 
           activity.resourceLink.includes(`/cuts/${cutId}?`);
  });

  if (isLoadingActivities) {
    return (
      <Card className="text-center py-12">
        <Loader2 className="w-8 h-8 text-primary mx-auto mb-4 animate-spin" />
        <p className="text-muted">Loading activity...</p>
      </Card>
    );
  }

  if (cutActivities.length === 0) {
    return (
      <Card className="text-center py-12">
        <Music className="w-12 h-12 text-muted mx-auto mb-4 opacity-50" />
        <p className="text-muted">No activity yet for this cut</p>
        <p className="text-sm text-muted mt-1">
          Actions like uploads, comments, and lyrics updates will appear here
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-1">
        {cutActivities.map((activity) => {
          const description = getActivityDescription(activity);
          const icon = getActivityIcon(activity.type);
          const iconColor = getActivityIconColor(activity.type);

          return (
            <button
              key={activity.id}
              onClick={async () => {
                if (!activity.isRead) await markActivityAsRead(activity.id);
                if (activity.resourceLink) navigate(activity.resourceLink);
              }}
              className="w-full text-left flex items-start gap-4 p-4 hover:bg-surface-light rounded-lg transition-colors border-b border-border last:border-b-0"
            >
              {/* Icon */}
              <div className={`p-3 rounded-lg ${iconColor} flex-shrink-0`}>
                {icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-text">
                  <span className="font-medium">{activity.user.name}</span>{' '}
                  {description}
                </p>
                <p className="text-xs text-muted mt-1">
                  {formatTimeAgo(activity.createdAt)}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted text-center">
          Showing {cutActivities.length} action{cutActivities.length !== 1 ? 's' : ''} for this cut
        </p>
      </div>
    </Card>
  );
}

export function CutOverview({ cutId }: CutOverviewProps) {
  const [subTab, setSubTab] = useState<OverviewSubTab>('activity');

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <Tabs
        tabs={[
          { id: 'activity', label: 'Activity' },
          { id: 'approvals', label: 'Approvals' },
        ]}
        activeTab={subTab}
        onChange={(tabId) => setSubTab(tabId as OverviewSubTab)}
      />

      {/* Content */}
      <TabPanel id="activity" activeTab={subTab}>
        <ActivityFeed cutId={cutId} />
      </TabPanel>

      <TabPanel id="approvals" activeTab={subTab}>
        <CutApprovals cutId={cutId} />
      </TabPanel>
    </div>
  );
}

// Keep CutManifest as an alias for backward compatibility
export { CutOverview as CutManifest };
